import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useClickOutside } from '../../hooks/useClickOutside'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import { TmbControlsRow, TmbStratificationPill, TmbQuestionPill } from '../../components/TMBControls'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import BarChart from '../../components/Charts/BarChart'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { exportToPNG, exportToSVG, exportToCSV } from '../../utils/tmbExportUtils'
import {
    normalizeKey,
    safeNumber,
    findColumnIndex,
    findColumnIndexByRegex,
    parseMonthToValue,
    resolveMonthIdx,
    formatDateLabel,
    COLUMN_CANDIDATES
} from '../../utils/dataUtils'
import {
    MonthInfo,
    QuestionInfo,
    HuddleTotals,
    HUDDLE_COLORS,
    normalizeHuddleGroup,
    codeForQuestionName,
    generateMonthList,
    sortQuestions,
    filterAgreementQuestions,
    initializeSliderValues,
    getSliderIndicesFromValues,
    TMB_SLIDER_KEY,
    extractActualMonthValues,
    checkHasDataForRange
} from '../../utils/tmbUtils'
import { useTmbSlider } from '../../context/TmbSliderContext'
import { applyLaunchDateFilterToZohoData } from '../../utils/tmbLaunchDates'
import '../../styles/shared/index.css'
import './AgreementSingle.css'

const PAGE_HEADING_LINE_1 = 'Percent of Patients who "Strongly'
const PAGE_HEADING_LINE_2 = 'Agree" by Huddle'

const AgreementSingle: React.FC = () => {
    const { selectedHospitalIds, refreshKey } = useHospitalSelection()
    const { globalMonthList } = useTmbSlider()
    const [rawData, setRawData] = useState<any>(null)
    const [monthList, setMonthList] = useState<MonthInfo[]>([])
    const [actualMonths, setActualMonths] = useState<Set<number>>(new Set())
    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`
    const [sliderRange, setSliderRange] = usePersistedState<[number, number]>(persistenceKey, [0, 0])
    const [sliderStartIdx, sliderEndIdx] = getSliderIndicesFromValues(globalMonthList, sliderRange)
    const hasDataForRange = checkHasDataForRange(sliderRange[0], sliderRange[1], actualMonths)

    const [selectedQuestion, setSelectedQuestion] = usePersistedState<string>('agreement_single_question', '')
    const [stratification, setStratification] = usePersistedState<string>('agreement_single_stratification', 'ExcludeNotSure')
    const [questions, setQuestions] = useState<QuestionInfo[]>([])
    const chartContainerRef = useRef<HTMLDivElement>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const downloadMenuRef = useRef<HTMLDivElement>(null)

    const [chartCategories, setChartCategories] = useState<string[]>([])
    const [chartSeries, setChartSeries] = useState<{ name: string; data: number[] }[]>([])
    const [chartColors, setChartColors] = useState<string[]>([])

    const [columnIndices, setColumnIndices] = useState<{
        monthIdx: number
        questionNameIdx: number
        questionLabelIdx: number
        stratIdx: number
        huddleIdx: number
        numeratorIdx: number
        denominatorIdx: number
    } | null>(null)

    useClickOutside([downloadMenuRef], () => setMenuOpen(false), menuOpen)

    const handleDownloadPNG = async () => {
        if (!chartContainerRef.current) return
        const filename = `Agreement_Single_${selectedQuestion || 'Question'}`
        const subtitle = selectedQuestionData?.fullText || ''
        await exportToPNG(chartContainerRef.current, filename, 
            { title: `${PAGE_HEADING_LINE_1} ${PAGE_HEADING_LINE_2}`, subtitle },
            { cssContextClass: 'agreement-single-page', hideSelectors: ['.chart-subtitle'] }
        )
    }

    const handleDownloadCSV = () => {
        const headers = ['page', 'hospital_ids', 'stratification', 'stratification_label', 'date_start', 'date_end', 'question', 'question_text', 'huddle_group', 'numerator', 'denominator', 'percent']
        const csvRowsData: (string | number)[][] = []

        const dateStart = globalMonthList[sliderStartIdx]?.label || ''
        const dateEnd = globalMonthList[sliderEndIdx]?.label || ''
        const dateStartFormatted = formatDateLabel(dateStart)
        const dateEndFormatted = formatDateLabel(dateEnd)

        const stratLabel = stratification === 'ExcludeNotSure' ? 'Exclude "Not"' : 'Include "Not"'

        const questionCode = selectedQuestionData?.code || ''
        const questionText = selectedQuestionData?.fullText || ''

        const hospitalIds = Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join(',') : selectedHospitalIds

        const rows = rawData?.response?.result?.rows || []
        if (!rows.length || !columnIndices) {
            exportToCSV({ headers, rows: [], filename: `Agreement_Single_${selectedQuestion || 'Question'}` })
            return
        }

        const { monthIdx, questionNameIdx, stratIdx, huddleIdx, numeratorIdx, denominatorIdx } = columnIndices

        let filteredRows = rows
        if (monthIdx !== -1 && monthList.length > 0) {
            const minMonthVal = sliderRange[0] || 0
            const maxMonthVal = sliderRange[1] || 0
            if (minMonthVal > 0 || maxMonthVal > 0) {
                filteredRows = filteredRows.filter((r: any[]) => {
                    const v = parseMonthToValue(r[monthIdx])
                    return v >= minMonthVal && v <= maxMonthVal
                })
            }
        }
        if (selectedQuestion && questionNameIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => String(r[questionNameIdx] || '').trim() === selectedQuestion)
        }
        if (stratIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => normalizeKey(r[stratIdx]) === normalizeKey(stratification))
        }

        const totals: { [key: string]: { num: number; denom: number } } = {
            'Yes Huddle': { num: 0, denom: 0 },
            'No Huddle': { num: 0, denom: 0 }
        }

        filteredRows.forEach((r: any[]) => {
            if (huddleIdx === -1) return
            const g = normalizeHuddleGroup(r[huddleIdx])
            if (!totals[g]) return
            const num = numeratorIdx >= 0 ? safeNumber(r[numeratorIdx]) : null
            const denom = denominatorIdx >= 0 ? safeNumber(r[denominatorIdx]) : null
            if (num !== null) totals[g].num += num
            if (denom !== null) totals[g].denom += denom
        })

        const huddleGroups = ['Yes Huddle', 'No Huddle']
        huddleGroups.forEach(huddle => {
            const t = totals[huddle]
            const pct = t.denom > 0 ? Math.round((t.num / t.denom) * 100) : 0
            csvRowsData.push([
                'agreement_single', hospitalIds, stratification, stratLabel,
                dateStartFormatted, dateEndFormatted, questionCode, questionText,
                huddle, t.num, t.denom, `${pct}%`
            ])
        })

        exportToCSV({ headers, rows: csvRowsData, filename: `Agreement_Single_${selectedQuestion || 'Question'}` })
    }

    const handleDownloadSVG = async () => {
        if (!chartContainerRef.current) return
        const filename = `Agreement_Single_${selectedQuestion || 'Question'}`
        const subtitle = selectedQuestionData?.fullText || ''
        await exportToSVG(chartContainerRef.current, filename, 
            { title: `${PAGE_HEADING_LINE_1} ${PAGE_HEADING_LINE_2}`, subtitle },
            { cssContextClass: 'agreement-single-page', hideSelectors: ['.chart-subtitle'] }
        )
    }
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    useEffect(() => {
        if (!selectedHospitalIds || selectedHospitalIds.length === 0) {
            setInitialLoading(false)
            setRawData(null)
            setMonthList([])
            setQuestions([])
            setError(null)
            return
        }

        const hospitalIdsParam = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(',')
            : selectedHospitalIds

        setLoading(true)
        setError(null)
        setInitialLoading(true)

        fetch(`/api/tmb/agreement_single/${hospitalIdsParam}`)
            .then(res => {
                if (!res.ok) {
                    if (res.status === 404) {
                        throw new Error('No data available for selected hospital(s)')
                    }
                    return res.json().then(errData => {
                        throw new Error(errData.error || `API error: ${res.status}`)
                    })
                }
                return res.json()
            })
            .then(data => {
                applyLaunchDateFilterToZohoData(data)
                const rows = data?.response?.result?.rows || []
                const columns = data?.response?.result?.column_order || []

                if (rows.length === 0 || !columns.length) {
                    setError('No data available for selected hospital(s)')
                    setLoading(false)
                    setInitialLoading(false)
                    return
                }

                setRawData(data)

                const monthIdx = resolveMonthIdx(columns, rows)
                const questionNameIdx = findColumnIndex(columns, COLUMN_CANDIDATES.questionName) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.questionName)
                    : findColumnIndexByRegex(columns, [/questionname/])
                const questionLabelIdx = findColumnIndex(columns, COLUMN_CANDIDATES.questionLabel) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.questionLabel)
                    : findColumnIndexByRegex(columns, [/questionlabel/, /questiontext/, /question/, /itemtext/, /item/])
                const stratIdx = findColumnIndex(columns, COLUMN_CANDIDATES.stratification) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.stratification)
                    : findColumnIndexByRegex(columns, [/strat/])
                const huddleIdx = findColumnIndex(columns, COLUMN_CANDIDATES.huddle) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.huddle)
                    : findColumnIndexByRegex(columns, [/huddlegroup/, /huddle/])
                const numeratorIdx = findColumnIndex(columns, COLUMN_CANDIDATES.numerator) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.numerator)
                    : findColumnIndexByRegex(columns, [/numerator/, /num/])
                const denominatorIdx = findColumnIndex(columns, COLUMN_CANDIDATES.denominator) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.denominator)
                    : findColumnIndexByRegex(columns, [/denominator/, /denom/])

                setColumnIndices({
                    monthIdx,
                    questionNameIdx,
                    questionLabelIdx,
                    stratIdx,
                    huddleIdx,
                    numeratorIdx,
                    denominatorIdx
                })

                setActualMonths(extractActualMonthValues(rows, monthIdx))
                const months = generateMonthList(rows, monthIdx)
                setMonthList(months)

                if (months.length > 0) {
                    setSliderRange(prev => initializeSliderValues(months, prev, 12))
                }

                const questionMap = new Map<string, QuestionInfo>()

                rows.forEach((row: any[]) => {
                    if (!row || row.length === 0) return

                    const name = questionNameIdx >= 0 && row[questionNameIdx]
                        ? String(row[questionNameIdx]).trim()
                        : ""

                    if (!name) return

                    if (!questionMap.has(name)) {
                        const code = codeForQuestionName(name) || name
                        const fullText = questionLabelIdx >= 0 && row[questionLabelIdx]
                            ? String(row[questionLabelIdx]).trim()
                            : name

                        questionMap.set(name, { name, code, fullText })
                    }
                })

                const questionList = filterAgreementQuestions(sortQuestions(Array.from(questionMap.values())))

                setQuestions(questionList)

                if (questionList.length > 0) {
                    const selectedExists = questionList.some(q => q.name === selectedQuestion)
                    if (!selectedQuestion || !selectedExists) {
                        setSelectedQuestion(questionList[0].name)
                    }
                }

                setLoading(false)
                setInitialLoading(false)
            })
            .catch(err => {
                setError(err.message || 'Failed to load data')
                setLoading(false)
                setInitialLoading(false)
            })
    }, [selectedHospitalIds, refreshKey])

    useEffect(() => {
        if (!rawData || !columnIndices) {
            setChartCategories([])
            setChartSeries([])
            setChartColors([])
            return
        }

        const rows = rawData?.response?.result?.rows || []
        if (!rows.length) return

        const { monthIdx, questionNameIdx, questionLabelIdx: _questionLabelIdx, stratIdx, huddleIdx, numeratorIdx, denominatorIdx } = columnIndices

        let filteredRows = rows
        if (monthIdx !== -1 && monthList.length > 0) {
            const minMonthVal = sliderRange[0] || 0
            const maxMonthVal = sliderRange[1] || 0

            if (minMonthVal > 0 || maxMonthVal > 0) {
                filteredRows = filteredRows.filter((r: any[]) => {
                    const v = parseMonthToValue(r[monthIdx])
                    return v >= minMonthVal && v <= maxMonthVal
                })
            }
        }

        if (selectedQuestion && questionNameIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => {
                const rowName = String(r[questionNameIdx] || "").trim()
                return rowName === selectedQuestion
            })
        }

        if (stratIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => {
                return normalizeKey(r[stratIdx]) === normalizeKey(stratification)
            })
        }

        if (!filteredRows.length) {
            setChartCategories(['No Huddle\n0', 'Yes Huddle\n0'])
            setChartSeries([{ name: 'Strongly Agree', data: [0, 0] }])
            setChartColors([HUDDLE_COLORS['No Huddle'], HUDDLE_COLORS['Yes Huddle']])
            return
        }

        const totals: HuddleTotals = {
            'Yes Huddle': { num: 0, denom: 0 },
            'No Huddle': { num: 0, denom: 0 }
        }

        filteredRows.forEach((r: any[]) => {
            if (huddleIdx === -1) return
            const g = normalizeHuddleGroup(r[huddleIdx])
            if (!totals[g]) return

            const num = numeratorIdx >= 0 ? safeNumber(r[numeratorIdx]) : null
            const denom = denominatorIdx >= 0 ? safeNumber(r[denominatorIdx]) : null
            if (num !== null) totals[g].num += num
            if (denom !== null) totals[g].denom += denom
        })

        const baseGroups = ['No Huddle', 'Yes Huddle']
        const categories: string[] = []
        const values: number[] = []
        const colors: string[] = []

        baseGroups.forEach(g => {
            const t = totals[g]
            const label = `${g}\n${t.denom}`
            categories.push(label)
            colors.push(HUDDLE_COLORS[g as keyof typeof HUDDLE_COLORS])
            let pct = 0
            if (t.denom > 0) pct = (t.num / t.denom) * 100
            values.push(pct)
        })

        setChartCategories(categories)
        setChartSeries([{ name: 'Strongly Agree', data: values }])
        setChartColors(colors)
    }, [rawData, columnIndices, monthList, sliderRange, selectedQuestion, stratification])

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const selectedQuestionData = questions.find(q => q.name === selectedQuestion)

    if (initialLoading) {
        return <LoadingNotice title="Loading agreement data..." />
    }

    if (error) {
        return (
            <div className="agreement-single-page tmb-page">
                <div className="content-wrapper">
                    <div className="header-wrapper">
                        <span className="page-title title--fixed">
                            <span className="title-line">{PAGE_HEADING_LINE_1}</span>
                            <span className="title-line">{PAGE_HEADING_LINE_2}</span>
                        </span>
                    </div>
                    <div className="section-card" style={{ minHeight: 'auto' }}>
                        {error.includes('No data available') ? (
                            <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                                No data available
                            </div>
                        ) : (
                            <p>Error: {error}</p>
                        )}
                    </div>
                </div>
            </div>
        )
    }
const isMobile = screenWidth < 768;

    return (
        <div id={generatePDFIds.dashboard()} className="agreement-single-page tmb-page">
            <div className="content-wrapper">
                <div className="header-wrapper header--stratified">
                    <span className="page-title title--fixed">
                        <span className="title-line">{PAGE_HEADING_LINE_1}</span>
                        <span className="title-line">{PAGE_HEADING_LINE_2}</span>
                    </span>

                    <div className="controls-col controls--row">
                        <TmbControlsRow>
                            {globalMonthList.length > 0 && (
                                <DateRangeSlider
                                    monthList={globalMonthList}
                                    startIndex={sliderStartIdx}
                                    endIndex={sliderEndIdx}
                                    onChange={(start, end) => setSliderRange([globalMonthList[start]?.value || 0, globalMonthList[end]?.value || 0])}
                                />
                            )}

                            <TmbStratificationPill
                                value={stratification}
                                onChange={setStratification}
                            />

                            <TmbQuestionPill
                                questions={questions}
                                selectedQuestion={selectedQuestion}
                                onSelect={setSelectedQuestion}
                            />
                        </TmbControlsRow>
                    </div>
                </div>

                <div className="section-card" ref={chartContainerRef} style={!hasDataForRange ? { minHeight: 'auto' } : undefined}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            Loading chart...
                        </div>
                    ) : !chartSeries.length || !chartSeries[0]?.data?.length || !hasDataForRange ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                            No data available
                        </div>
                    ) : (
                        <>
                            <div className="custom-download-menu" ref={downloadMenuRef}>
                                <div className="hamburger-icon" onClick={() => setMenuOpen(!menuOpen)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                        <g>
                                            <rect x="4" y="6" width="16" height="2" rx="1" />
                                            <rect x="4" y="11" width="16" height="2" rx="1" />
                                            <rect x="4" y="16" width="16" height="2" rx="1" />
                                        </g>
                                    </svg>
                                </div>
                                {menuOpen && (
                                    <div className="download-dropdown show">
                                        <div className="menu-items-row">
                                            <div className="menu-item" onClick={async () => { await handleDownloadPNG(); setMenuOpen(false); }}>Download PNG</div>
                                            <div className="menu-item" onClick={async () => { await handleDownloadSVG(); setMenuOpen(false); }}>Download SVG</div>
                                            <div className="menu-item" onClick={() => { handleDownloadCSV(); setMenuOpen(false); }}>Download CSV</div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="chart-subtitle" id="agreement-question-text">
                                {selectedQuestionData?.fullText || 'Select a question'}
                            </div>
                            <div id="agreementChart" className="chart-container">
                                <div  style={{width:"80%", margin:"auto"}}>
                                <BarChart
                                    categories={chartCategories}
                                    series={chartSeries}
                                    colors={chartColors}
                                    height={355}
                                    yAxisMin={0}
                                    yAxisMax={100}
                                    yAxisShow={false}
                                    dataLabelsEnabled={true}
                                    dataLabelSuffix="%"
                                    showToolbar={false}
                                    showGrid={false}
                                    showXAxisBorder={false}
                                    showLegend={false}
                                    gridPadding={{
                                        bottom: 0,
                                        left: isMobile ? 5 : screenWidth < 1200 ? 150 : 250,
                                        right: isMobile ? 5 : screenWidth < 1200 ? -50 : 50
                                    }}
                                    xAxisMultiLine={true}
                                    fontFamily="Poppins, Segoe UI, sans-serif"
                                    plotOptionsBar={{
                                        columnWidth: isMobile ? '30%' : '70%',
                                        borderRadius: 2,
                                        distributed: true,
                                        dataLabels: {
                                            position: 'top',
                                        }
                                    }}
                                    dataLabelsOffsetY={-40}
                                    dataLabelsColor="#000000"
                                    dataLabelsFontWeight='400'
                                    dataLabelsFontSize="16"
                                    showlabelsForBars={true}
                                />
                                </div>
                                <div className="n-label">N=</div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AgreementSingle
